import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SearchIcon from '@mui/icons-material/Search';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExerciseEditorDialog from './../exercises/ExerciseEditorDialog';
import WorkoutExerciseSearch from './WorkoutExerciseSearch';
import Loading from '../shared/Loading';
import ServerError from '../error/ServerError';
import { mongoObjectId } from '../../utils/mongoObjectId';
import { SEARCH_EXERCISES } from '../../graphql/exercise';
import { useLazyQuery } from '@apollo/client/react';

const groupAccent = (index) => {
  if (index === 1) return '#6254e8';
  if (index === 2) return '#f97316';
  return '#4338ca';
};

const exerciseImage = (exercise, size = 64, aspectRatio = 0.72) => {
  const url = exercise?.images?.[0]?.url;
  if (!url) {
    return (
      <Box
        sx={{
          width: size,
          height: size * aspectRatio,
          borderRadius: 1,
          backgroundColor: '#101820',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FitnessCenterIcon fontSize="small" />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={url}
      alt={exercise.title}
      sx={{
        width: size,
        height: size * aspectRatio,
        borderRadius: 1,
        objectFit: 'cover',
        backgroundColor: '#101820',
      }}
    />
  );
};

const exerciseCount = (groups) =>
  (groups ?? []).reduce(
    (total, group) => total + (group.exercises?.length ?? 0),
    0
  );

const estimatedVolume = (groups) =>
  (groups ?? []).reduce(
    (total, group) =>
      total +
      (group.exercises ?? []).reduce(
        (sum, exercise) =>
          sum +
          (exercise.sets ?? 1) * (exercise.reps ?? 1) * (exercise.weight || 0),
        0
      ),
    0
  );

const WorkoutStat = ({ icon, value, label, children }) => (
  <Box
    sx={{
      px: 3,
      py: 2,
      flex: 1,
      minWidth: 180,
      borderRight: '1px solid #dbe3ee',
      '&:last-of-type': { borderRight: 0 },
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      {icon}
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: 24, lineHeight: 1.15 }}>
          {value}
        </Typography>
        <Typography sx={{ color: '#40536b', fontSize: 13 }}>{label}</Typography>
      </Box>
      {children}
    </Stack>
  </Box>
);

const parseInteger = (value, min = 0) => {
  if (value === '') {
    return null;
  }

  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return Math.max(min, parsed);
};

const exerciseFieldStyles = {
  '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: 1 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dbe3ee' },
  '& .MuiInputLabel-root': { color: '#65758b' },
};

const ExerciseField = ({ children }) => (
  <Box
    sx={{
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      '@container (min-width: 1000px)': {
        borderLeft: '1px solid #e5eaf2',
        pl: 2,
      },
    }}
  >
    {children}
  </Box>
);

const DragHandle = ({
  label,
  onDragStart,
  onDragEnd,
  onMove,
  color = '#8090a3',
}) => (
  <IconButton
    size="small"
    draggable
    aria-label={label}
    title={onMove ? `${label}. Use arrow keys to move up or down.` : label}
    onClick={(event) => event.stopPropagation()}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onKeyDown={(event) => {
      if (onMove && ['ArrowUp', 'ArrowDown'].includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        onMove(event.key === 'ArrowUp' ? -1 : 1);
      }
    }}
    sx={{
      color,
      cursor: 'grab',
      flexShrink: 0,
      '&:active': { cursor: 'grabbing' },
    }}
  >
    <DragIndicatorIcon sx={{ fontSize: 20, pointerEvents: 'none' }} />
  </IconButton>
);

const ExerciseRow = ({
  exercise,
  onDurationChange,
  onRestAfterExerciseDurationChange,
  onWeightChange,
  onWeightUnitChange,
  onRepsChange,
  onIsTimedChange,
  dragHandleProps,
  dropProps,
  dropStyles,
  isDragging,
  onDelete,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuId = `exercise-actions-${exercise._id}`;
  return (
    <Box
      {...dropProps}
      sx={{
        ...dropStyles,
        opacity: isDragging ? 0.45 : 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 2,
        px: { xs: 2, md: 2.5 },
        py: 3,
        borderTop: '1px solid #e5eaf2',
        alignItems: 'stretch',
        position: 'relative',
        '@container (min-width: 650px)': {
          gridTemplateColumns: '1.2fr 0.9fr 1.4fr 1.6fr',
        },
        '@container (min-width: 1000px)': {
          gridTemplateColumns:
            'minmax(230px, 1.7fr) 1fr 0.8fr 1.25fr 1.4fr 32px',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          minWidth: 0,
          gridColumn: '1 / -1',
          pr: 4,
          '@container (min-width: 1000px)': { gridColumn: 'auto', pr: 0 },
          '& img, & > .MuiBox-root': { flexShrink: 0 },
        }}
      >
        <DragHandle label={`Drag ${exercise.title}`} {...dragHandleProps} />
        {exerciseImage(exercise, 72, 1)}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 800, fontSize: 16, overflowWrap: 'anywhere' }}
          >
            {exercise.title}
          </Typography>
          {!!exercise.percentOf1RM && (
            <Typography sx={{ color: '#65758b', fontSize: 13 }}>
              {exercise.percentOf1RM}% 1RM
            </Typography>
          )}
        </Box>
      </Stack>
      <ExerciseField>
        <TextField
          fullWidth
          select
          size="small"
          label="Exercise mode"
          value={exercise.isTimed ? 'timed' : 'reps'}
          onChange={(event) => onIsTimedChange(event.target.value === 'timed')}
          SelectProps={{ native: true }}
          sx={exerciseFieldStyles}
        >
          <option value="reps">Rep based</option>
          <option value="timed">Timed</option>
        </TextField>
      </ExerciseField>
      <ExerciseField>
        {exercise.isTimed ? (
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Duration"
            value={exercise.duration ?? ''}
            onChange={(event) =>
              onDurationChange(parseInteger(event.target.value, 1))
            }
            InputProps={{
              inputProps: { min: 1, max: 1000 },
              endAdornment: <InputAdornment position="end">sec</InputAdornment>,
            }}
            sx={exerciseFieldStyles}
          />
        ) : (
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Reps"
            value={exercise.reps ?? ''}
            onChange={(event) =>
              onRepsChange(parseInteger(event.target.value, 1))
            }
            inputProps={{ min: 1 }}
            sx={exerciseFieldStyles}
          />
        )}
      </ExerciseField>
      <ExerciseField>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Weight"
          value={exercise.weight ?? ''}
          onChange={(event) =>
            onWeightChange(parseInteger(event.target.value, 0))
          }
          InputProps={{
            inputProps: { min: 0 },
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 1 }}>
                <Box
                  component="select"
                  aria-label="Weight unit"
                  value={exercise.weightUnit || 'lbs'}
                  onChange={(event) => onWeightUnitChange(event.target.value)}
                  sx={{
                    border: 0,
                    borderLeft: '1px solid #e5eaf2',
                    borderRadius: 0,
                    pl: 1,
                    py: 0.5,
                    width: 64,
                    font: 'inherit',
                    color: '#263238',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <option value="lbs">lbs</option>
                  <option value="kg">kg</option>
                </Box>
              </InputAdornment>
            ),
          }}
          sx={exerciseFieldStyles}
        />
      </ExerciseField>
      <ExerciseField>
        <Stack spacing={1} sx={{ pt: 0.5 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Rest"
            value={exercise.restAfterExerciseDuration ?? ''}
            onChange={(event) =>
              onRestAfterExerciseDurationChange(
                parseInteger(event.target.value, 0)
              )
            }
            InputProps={{
              inputProps: { min: 0, max: 1000 },
              startAdornment: (
                <InputAdornment position="start">
                  <TimerOutlinedIcon sx={{ color: '#65758b', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">sec</InputAdornment>,
            }}
            sx={exerciseFieldStyles}
          />
          <Stack direction="row" spacing={0.75}>
            {[
              [30, '30s'],
              [60, '60s'],
              [90, '90s'],
              [120, '2m'],
            ].map(([seconds, label]) => (
              <Button
                key={seconds}
                size="small"
                aria-label={`Rest ${label}`}
                aria-pressed={exercise.restAfterExerciseDuration === seconds}
                onClick={() => onRestAfterExerciseDurationChange(seconds)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderRadius: 5,
                  px: 0.75,
                  py: 0.25,
                  textTransform: 'none',
                  fontWeight: 500,
                  backgroundColor:
                    exercise.restAfterExerciseDuration === seconds
                      ? '#a394f5'
                      : '#f1f3f6',
                  color:
                    exercise.restAfterExerciseDuration === seconds
                      ? '#fff'
                      : '#526178',
                  '&:hover': {
                    backgroundColor:
                      exercise.restAfterExerciseDuration === seconds
                        ? '#8c7ae6'
                        : '#e5eaf2',
                  },
                }}
              >
                {label}
              </Button>
            ))}
          </Stack>
        </Stack>
      </ExerciseField>
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 12,
          '@container (min-width: 1000px)': {
            position: 'static',
            alignSelf: 'center',
          },
        }}
      >
        <IconButton
          size="small"
          aria-label={`More options for ${exercise.title}`}
          aria-haspopup="menu"
          aria-controls={menuAnchor ? menuId : undefined}
          aria-expanded={menuAnchor ? 'true' : undefined}
          onClick={(event) => {
            event.stopPropagation();
            setMenuAnchor(event.currentTarget);
          }}
          sx={{ color: '#65758b' }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          id={menuId}
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          onClick={(event) => event.stopPropagation()}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDelete();
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            Delete exercise
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

const ExerciseBlock = ({
  group,
  index,
  onAddExercise,
  onRestBetweenRoundsChange,
  onExerciseDurationChange,
  onExerciseRestAfterDurationChange,
  onExerciseWeightChange,
  onExerciseWeightUnitChange,
  onExerciseRepsChange,
  onExerciseIsTimedChange,
  dragControls,
  onDeleteExercise,
}) => {
  const accent = groupAccent(index);
  const isPlainBlock = !group.groupName;
  const title = isPlainBlock ? 'Exercises' : group.groupName;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: '#dbe3ee',
        borderRadius: 1,
        containerType: 'inline-size',
      }}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Stack
          {...dragControls.dropProps({ type: 'group', groupId: group._id })}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2.5,
            py: 2.25,
            flexWrap: 'wrap',
            gap: 2,
            ...dragControls.dropStyles({ type: 'group', groupId: group._id }),
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            {!isPlainBlock && (
              <>
                <DragHandle
                  label={`Drag ${title}`}
                  color={accent}
                  {...dragControls.handleProps({
                    type: 'group',
                    groupId: group._id,
                  })}
                />
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 2,
                    backgroundColor: accent,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ViewModuleIcon sx={{ fontSize: 17 }} />
                </Box>
              </>
            )}
            <Typography sx={{ fontWeight: 800, fontSize: 20, color: accent }}>
              {title}
            </Typography>
            {!isPlainBlock && (
              <Chip
                size="small"
                label={`${group.exercises?.length ?? 0} Exercises`}
                sx={{
                  backgroundColor: `${accent}1a`,
                  color: accent,
                  fontWeight: 700,
                }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={3} alignItems="center">
            {!isPlainBlock && (
              <>
                <Typography sx={{ color: '#40536b', fontWeight: 700 }}>
                  {group.rounds ?? 1} Rounds
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  label="Circuit rest"
                  value={group.restBetweenRounds ?? 0}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) =>
                    onRestBetweenRoundsChange(
                      group._id,
                      parseInteger(event.target.value, 0)
                    )
                  }
                  InputProps={{
                    inputProps: { min: 0, max: 1000 },
                    endAdornment: (
                      <InputAdornment position="end">sec</InputAdornment>
                    ),
                  }}
                  sx={{ width: 156 }}
                />
              </>
            )}
          </Stack>
        </Stack>
        {(group.exercises ?? []).map((exercise, exerciseIndex) => (
          <ExerciseRow
            key={exercise._id}
            exercise={exercise}
            onDelete={() => onDeleteExercise(group._id, exercise._id)}
            dragHandleProps={dragControls.handleProps({
              type: 'exercise',
              groupId: group._id,
              exerciseId: exercise._id,
            })}
            dropProps={dragControls.dropProps({
              type: 'exercise',
              groupId: group._id,
              exerciseId: exercise._id,
            })}
            dropStyles={dragControls.dropStyles({
              type: 'exercise',
              groupId: group._id,
              exerciseId: exercise._id,
            })}
            isDragging={
              dragControls.item?.type === 'exercise' &&
              dragControls.item.exerciseId === exercise._id
            }
            onDurationChange={(duration) =>
              onExerciseDurationChange(group._id, exerciseIndex, duration)
            }
            onRestAfterExerciseDurationChange={(duration) =>
              onExerciseRestAfterDurationChange(
                group._id,
                exerciseIndex,
                duration
              )
            }
            onWeightChange={(weight) =>
              onExerciseWeightChange(group._id, exerciseIndex, weight)
            }
            onWeightUnitChange={(weightUnit) =>
              onExerciseWeightUnitChange(group._id, exerciseIndex, weightUnit)
            }
            onRepsChange={(reps) =>
              onExerciseRepsChange(group._id, exerciseIndex, reps)
            }
            onIsTimedChange={(isTimed) =>
              onExerciseIsTimedChange(group._id, exerciseIndex, isTimed)
            }
          />
        ))}
        <Button
          {...dragControls.dropProps({ type: 'exercise', groupId: group._id })}
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={(event) => {
            event.stopPropagation();
            onAddExercise();
          }}
          sx={{
            ...dragControls.dropStyles({
              type: 'exercise',
              groupId: group._id,
            }),
            borderRadius: 0,
            borderStyle: 'dashed',
            borderLeft: 0,
            borderRight: 0,
            borderBottom: 0,
            py: 2,
            borderColor: '#b7b2e8',
          }}
        >
          Add Exercise
        </Button>
      </CardContent>
    </Card>
  );
};

const ExerciseLibrary = ({
  onSelect,
  onAddNew,
  createdExercises = [],
  dragControls,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [search, { loading, error, data }] = useLazyQuery(SEARCH_EXERCISES);
  const delayedSearch = useCallback(_.debounce(search, 500), [search]);

  useEffect(() => {
    delayedSearch({
      variables: {
        input: {
          searchTerm: searchQuery,
          limit: 8,
        },
      },
    });
  }, [delayedSearch, searchQuery]);

  const exercises = [
    ...createdExercises,
    ...(data?.exercises ?? []).filter(
      (exercise) =>
        !createdExercises.some((created) => created._id === exercise._id)
    ),
  ];

  return (
    <Card variant="outlined" sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
            Exercise Library
          </Typography>
        </Stack>
        <TextField
          fullWidth
          size="small"
          placeholder="Search exercises..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#65758b', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />
        <ToggleButtonGroup value="all" exclusive size="small" sx={{ mb: 2 }}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="recent">Recent</ToggleButton>
          <ToggleButton value="favorites">Favorites</ToggleButton>
        </ToggleButtonGroup>
        {!!loading && <Loading circular />}
        {!!error && <ServerError message={error.message} />}
        <Stack spacing={1}>
          {exercises.map((exercise) => (
            <Stack
              key={exercise._id}
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ pb: 1, borderBottom: '1px solid #edf1f6' }}
            >
              <DragHandle
                label={`Drag ${exercise.title} from library`}
                {...dragControls.handleProps({ type: 'library', exercise })}
              />
              {exerciseImage(exercise, 42)}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontWeight: 800, fontSize: 14 }}>
                  {exercise.title}
                </Typography>
                <Typography noWrap sx={{ color: '#40536b', fontSize: 13 }}>
                  {exercise.equipment || exercise.tags?.[0] || 'Exercise'}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label={`Add ${exercise.title}`}
                onClick={() => onSelect(exercise)}
                sx={{ border: '1px solid #d0d9e6', borderRadius: 1 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Button startIcon={<AddIcon />} onClick={onAddNew} sx={{ mt: 1.5 }}>
          Create New Exercise
        </Button>
      </CardContent>
    </Card>
  );
};

const WorkoutContent = ({ workout, setWorkout }) => {
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [addExerciseOpen, setAddExerciseOpen] = useState(false);
  const [openExerciseCreator, setOpenExerciseCreator] = useState(false);
  const [createdExercises, setCreatedExercises] = useState([]);
  const [dragItem, setDragItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const groups = useMemo(() => workout?.exerciseGroups ?? [], [workout]);

  useEffect(() => {
    if (!selectedGroupId && groups[0]?._id) {
      setSelectedGroupId(groups[0]._id);
    }
  }, [groups, selectedGroupId]);

  if (!workout) {
    return <Loading />;
  }

  const ensureGroup = () => {
    if (workout.exerciseGroups?.length) {
      return workout.exerciseGroups;
    }

    return [
      {
        _id: mongoObjectId(),
        groupName: '',
        rounds: 1,
        restBetweenRounds: 0,
        exercises: [],
      },
    ];
  };

  const handleAddGroup = (template = {}) => {
    const newGroup = {
      _id: mongoObjectId(),
      groupName: template.groupName ?? '',
      rounds: template.rounds ?? 1,
      restBetweenRounds: template.restBetweenRounds ?? 0,
      exercises: [],
    };
    const exerciseGroups = [...groups, newGroup];
    setSelectedGroupId(newGroup._id);
    setWorkout({ ...workout, exerciseGroups });
  };

  const handleAddExerciseOpen = (group) => {
    setSelectedGroupId(group._id);
    setAddExerciseOpen(true);
  };

  const handleAddExerciseClose = () => {
    setAddExerciseOpen(false);
  };

  const handleCreateExerciseOpen = () => {
    setAddExerciseOpen(false);
    setOpenExerciseCreator(true);
  };

  const addExerciseToSelectedGroup = (
    exercise,
    destinationGroupId,
    destinationIndex
  ) => {
    const copy = _.cloneDeep({
      ...exercise,
      duration: 10,
      reps: 10,
      sets: 1,
      percentOf1RM: 0,
      weight: 0,
      weightUnit: 'lbs',
      restBetweenSetsDuration: 0,
      restAfterExerciseDuration: 0,
      isTimed: false,
    });
    copy.exerciseId = exercise._id;
    copy._id = mongoObjectId();

    const existingGroups = ensureGroup();
    const targetGroupId =
      destinationGroupId ?? selectedGroupId ?? existingGroups[0]._id;
    const exerciseGroups = existingGroups.map((group) => {
      if (group._id !== targetGroupId) return group;
      const exercises = [...(group.exercises ?? [])];
      exercises.splice(destinationIndex ?? exercises.length, 0, copy);
      return {
        ...group,
        exercises,
      };
    });
    setWorkout({ ...workout, exerciseGroups });
  };

  const handleDrawerExerciseSelected = (exercise) => {
    addExerciseToSelectedGroup(exercise);
    handleAddExerciseClose();
  };

  const handleExerciseCreated = (exercise) => {
    setOpenExerciseCreator(false);
    if (!exercise?._id) {
      return;
    }
    setCreatedExercises((current) => [
      exercise,
      ...current.filter((item) => item._id !== exercise._id),
    ]);
  };

  const handleExerciseDurationChange = (groupId, exerciseIndex, duration) => {
    const exerciseGroups = ensureGroup().map((group) => {
      if (group._id !== groupId) {
        return group;
      }

      return {
        ...group,
        exercises: (group.exercises ?? []).map((exercise, index) => {
          if (index !== exerciseIndex) {
            return exercise;
          }

          return {
            ...exercise,
            duration,
          };
        }),
      };
    });

    setWorkout({ ...workout, exerciseGroups });
  };

  const handleExerciseRestAfterDurationChange = (
    groupId,
    exerciseIndex,
    duration
  ) => {
    const exerciseGroups = ensureGroup().map((group) => {
      if (group._id !== groupId) {
        return group;
      }

      return {
        ...group,
        exercises: (group.exercises ?? []).map((exercise, index) => {
          if (index !== exerciseIndex) {
            return exercise;
          }

          return {
            ...exercise,
            restAfterExerciseDuration: duration,
          };
        }),
      };
    });

    setWorkout({ ...workout, exerciseGroups });
  };

  const handleRestBetweenRoundsChange = (groupId, duration) => {
    const exerciseGroups = ensureGroup().map((group) => {
      if (group._id !== groupId) {
        return group;
      }

      return {
        ...group,
        restBetweenRounds: duration ?? 0,
      };
    });

    setWorkout({ ...workout, exerciseGroups });
  };

  const handleExerciseChange = (groupId, exerciseIndex, changes) => {
    const exerciseGroups = groups.map((group) =>
      group._id === groupId
        ? {
            ...group,
            exercises: (group.exercises ?? []).map((exercise, index) =>
              index === exerciseIndex ? { ...exercise, ...changes } : exercise
            ),
          }
        : group
    );
    setWorkout({ ...workout, exerciseGroups });
  };

  const handleDeleteExercise = (groupId, exerciseId) => {
    const exerciseGroups = groups.map((group) =>
      group._id === groupId
        ? {
            ...group,
            exercises: (group.exercises ?? []).filter(
              (exercise) => exercise._id !== exerciseId
            ),
          }
        : group
    );
    setWorkout({ ...workout, exerciseGroups });
  };

  const moveGroup = (sourceId, targetId, after = false) => {
    if (sourceId === targetId) return;
    const source = groups.find((group) => group._id === sourceId);
    if (!source || !groups.some((group) => group._id === targetId)) return;
    const exerciseGroups = groups.filter((group) => group._id !== sourceId);
    const index = exerciseGroups.findIndex((group) => group._id === targetId);
    exerciseGroups.splice(index + (after ? 1 : 0), 0, source);
    setWorkout({ ...workout, exerciseGroups });
  };

  const moveExercise = (item, target, after = false) => {
    if (
      item.groupId === target.groupId &&
      item.exerciseId === target.exerciseId
    )
      return;
    const sourceGroup = groups.find((group) => group._id === item.groupId);
    const exercise = sourceGroup?.exercises?.find(
      (entry) => entry._id === item.exerciseId
    );
    if (!exercise || !groups.some((group) => group._id === target.groupId))
      return;
    const exerciseGroups = groups.map((group) => {
      if (![item.groupId, target.groupId].includes(group._id)) return group;
      const exercises = (group.exercises ?? []).filter(
        (entry) => group._id !== item.groupId || entry._id !== item.exerciseId
      );
      if (group._id === target.groupId) {
        const targetIndex = exercises.findIndex(
          (entry) => entry._id === target.exerciseId
        );
        const index =
          targetIndex < 0 ? exercises.length : targetIndex + (after ? 1 : 0);
        exercises.splice(index, 0, exercise);
      }
      return { ...group, exercises };
    });
    setSelectedGroupId(target.groupId);
    setWorkout({ ...workout, exerciseGroups });
  };

  const clearDrag = () => {
    setDragItem(null);
    setDropTarget(null);
  };

  const canDrop = (target) =>
    dragItem && (dragItem.type !== 'group' || target.type === 'group');
  const dropAfter = (event, target) => {
    if (target.type === 'exercise' && !target.exerciseId) return true;
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientY > rect.top + rect.height / 2;
  };
  const sameTarget = (first, second) =>
    first?.type === second.type &&
    first?.groupId === second.groupId &&
    first?.exerciseId === second.exerciseId;

  const dragControls = {
    item: dragItem,
    handleProps: (item) => ({
      onDragStart: (event) => {
        event.stopPropagation();
        if (event.dataTransfer) {
          event.dataTransfer.effectAllowed =
            item.type === 'library' ? 'copy' : 'move';
          event.dataTransfer.setData(
            'text/plain',
            item.exerciseId || item.groupId || item.exercise._id
          );
        }
        setDragItem(item);
      },
      onDragEnd: clearDrag,
      onMove:
        item.type === 'library'
          ? undefined
          : (direction) => {
              const entries =
                item.type === 'group'
                  ? groups
                  : groups.find((group) => group._id === item.groupId)
                      ?.exercises ?? [];
              const index = entries.findIndex(
                (entry) =>
                  entry._id ===
                  (item.type === 'group' ? item.groupId : item.exerciseId)
              );
              const neighbor = entries[index + direction];
              if (!neighbor) return;
              if (item.type === 'group')
                moveGroup(item.groupId, neighbor._id, direction > 0);
              else
                moveExercise(
                  item,
                  {
                    type: 'exercise',
                    groupId: item.groupId,
                    exerciseId: neighbor._id,
                  },
                  direction > 0
                );
            },
    }),
    dropProps: (target) => ({
      onDragOver: (event) => {
        if (!canDrop(target)) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer)
          event.dataTransfer.dropEffect =
            dragItem.type === 'library' ? 'copy' : 'move';
        const after = dropAfter(event, target);
        setDropTarget((current) =>
          sameTarget(current, target) && current.after === after
            ? current
            : { ...target, after }
        );
      },
      onDragLeave: (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setDropTarget((current) =>
            sameTarget(current, target) ? null : current
          );
        }
      },
      onDrop: (event) => {
        if (!canDrop(target)) return;
        event.preventDefault();
        event.stopPropagation();
        const after = dropAfter(event, target);
        if (dragItem.type === 'group')
          moveGroup(dragItem.groupId, target.groupId, after);
        else if (dragItem.type === 'library') {
          const exercises =
            groups.find((group) => group._id === target.groupId)?.exercises ??
            [];
          const index = exercises.findIndex(
            (exercise) => exercise._id === target.exerciseId
          );
          addExerciseToSelectedGroup(
            dragItem.exercise,
            target.groupId,
            index < 0 ? exercises.length : index + (after ? 1 : 0)
          );
          setSelectedGroupId(target.groupId);
        } else moveExercise(dragItem, target, after);
        clearDrag();
      },
    }),
    dropStyles: (target) =>
      sameTarget(dropTarget, target)
        ? {
            backgroundColor: '#f5f3ff',
            boxShadow: `inset 0 ${dropTarget.after ? '-3px' : '3px'} 0 #6254e8`,
          }
        : {},
  };

  const duration = Math.max(1, Math.floor((workout.durationTotal ?? 0) / 60));
  const count = exerciseCount(groups);
  const volume = estimatedVolume(groups).toLocaleString();

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={9}>
          <Stack spacing={2}>
            <Card
              variant="outlined"
              sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
            >
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Stack direction="row" flexWrap="wrap">
                  <WorkoutStat
                    icon={<ScheduleIcon sx={{ color: '#4338ca' }} />}
                    value={duration}
                    label="Est. Duration"
                  />
                  <WorkoutStat
                    icon={<FitnessCenterIcon sx={{ color: '#4338ca' }} />}
                    value={count}
                    label="Exercises"
                  />
                  <WorkoutStat
                    icon={<ViewModuleIcon sx={{ color: '#4338ca' }} />}
                    value={volume}
                    label="Est. Volume"
                  />
                  <WorkoutStat value="Muscle Groups" label="">
                    <Stack direction="row" spacing={0.75} sx={{ ml: 1 }}>
                      {['Chest', 'Back', 'Shoulders', '+2'].map((label) => (
                        <Chip
                          key={label}
                          size="small"
                          label={label}
                          sx={{ fontWeight: 700 }}
                        />
                      ))}
                    </Stack>
                  </WorkoutStat>
                </Stack>
              </CardContent>
            </Card>

            {groups.length === 0 && (
              <Card
                variant="outlined"
                sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
              >
                <CardContent>
                  <Typography sx={{ fontWeight: 800, mb: 1 }}>
                    Exercises
                  </Typography>
                  <Typography sx={{ color: '#40536b', mb: 2 }}>
                    Add a circuit, then choose exercises from the library.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {groups.map((group, index) => (
              <Box
                key={group._id}
                onClick={() => setSelectedGroupId(group._id)}
              >
                <ExerciseBlock
                  onDeleteExercise={handleDeleteExercise}
                  dragControls={dragControls}
                  group={group}
                  index={index}
                  onAddExercise={() => handleAddExerciseOpen(group)}
                  onRestBetweenRoundsChange={handleRestBetweenRoundsChange}
                  onExerciseDurationChange={handleExerciseDurationChange}
                  onExerciseRestAfterDurationChange={
                    handleExerciseRestAfterDurationChange
                  }
                  onExerciseWeightChange={(groupId, exerciseIndex, weight) =>
                    handleExerciseChange(groupId, exerciseIndex, {
                      weight,
                    })
                  }
                  onExerciseWeightUnitChange={(
                    groupId,
                    exerciseIndex,
                    weightUnit
                  ) =>
                    handleExerciseChange(groupId, exerciseIndex, {
                      weightUnit,
                    })
                  }
                  onExerciseRepsChange={(groupId, exerciseIndex, reps) =>
                    handleExerciseChange(groupId, exerciseIndex, { reps })
                  }
                  onExerciseIsTimedChange={(groupId, exerciseIndex, isTimed) =>
                    handleExerciseChange(groupId, exerciseIndex, { isTimed })
                  }
                />
              </Box>
            ))}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ViewModuleIcon />}
                onClick={() =>
                  handleAddGroup({
                    groupName: 'Circuit',
                    rounds: 3,
                    restBetweenRounds: 0,
                  })
                }
              >
                Add Circuit
              </Button>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={3}>
          <Stack spacing={2}>
            <ExerciseLibrary
              dragControls={dragControls}
              onSelect={addExerciseToSelectedGroup}
              onAddNew={handleCreateExerciseOpen}
              createdExercises={createdExercises}
            />
            <Card
              variant="outlined"
              sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
            >
              <CardContent>
                <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 1.5 }}>
                  Workout Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  placeholder="Add notes about this workout..."
                  value={workout.desc ?? ''}
                  onChange={(event) =>
                    setWorkout({ ...workout, desc: event.target.value })
                  }
                  inputProps={{ maxLength: 500 }}
                  helperText={`${workout.desc?.length ?? 0} / 500`}
                />
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <ExerciseEditorDialog
        key="new"
        onSaved={handleExerciseCreated}
        open={openExerciseCreator}
        onClose={() => setOpenExerciseCreator(false)}
        exercise={{}}
      />
      <Drawer
        sx={{ zIndex: 2000 }}
        anchor="bottom"
        open={addExerciseOpen}
        onClose={handleAddExerciseClose}
      >
        <Box p={3}>
          <WorkoutExerciseSearch
            onSelect={handleDrawerExerciseSelected}
            onAddNew={handleCreateExerciseOpen}
            onClose={handleAddExerciseClose}
          />
        </Box>
      </Drawer>
    </Box>
  );
};

export default WorkoutContent;
